export type CreativePlacementId = 'header' | 'search' | 'universal'

export type CreativeImageFormat = 'png' | 'jpeg'

export interface CreativeSafeArea {
  width: number
  height: number
  /** Vertical shift of the safe area centre from the canvas centre, px (negative = up) */
  offsetY: number
}

export interface CreativePlacementSpec {
  id: CreativePlacementId
  width: number
  height: number
  /** PNG only (Universal) or PNG + JPEG (Header, Search Results) */
  formats: readonly CreativeImageFormat[]
  fileBase: string
  /** Third-party measurement of Apple's "ART SAFE AREA" template layer — unverified, see docs */
  safeArea: CreativeSafeArea
}

/** Data-driven so new Apple sizes only need a new row. */
export const CREATIVE_PLACEMENTS: readonly CreativePlacementSpec[] = [
  {
    id: 'header',
    width: 3840,
    height: 1646,
    formats: ['png', 'jpeg'],
    fileBase: 'header',
    safeArea: {width: 1646, height: 661, offsetY: 0},
  },
  {
    id: 'search',
    width: 3840,
    height: 2560,
    formats: ['png', 'jpeg'],
    fileBase: 'search-results',
    safeArea: {width: 2168, height: 1030, offsetY: 0},
  },
  {
    id: 'universal',
    width: 5244,
    height: 2950,
    formats: ['png'],
    fileBase: 'universal',
    safeArea: {width: 1402, height: 962, offsetY: -334},
  },
]

export const getPlacementSpec = (id: CreativePlacementId): CreativePlacementSpec =>
  CREATIVE_PLACEMENTS.find((p) => p.id === id) ?? CREATIVE_PLACEMENTS[0]

export const MAX_CREATIVE_SCREENSHOTS = 3

/** Width of the live preview canvas in px (the export renders at full spec size). */
export const CREATIVE_PREVIEW_WIDTH = 1280

export const JPEG_EXPORT_QUALITY = 0.95

export type CreativeTemplateId =
  | 'keyArt'
  | 'fan'
  | 'cascade'
  | 'strip'
  | 'splitText'
  | 'iconHeadline'
  | 'iconStack'
  | 'gameplay'

export const CREATIVE_TEMPLATES: readonly CreativeTemplateId[] = [
  'keyArt',
  'fan',
  'cascade',
  'strip',
  'splitText',
  'iconHeadline',
  'iconStack',
  'gameplay',
]

/** Which inputs a layout actually uses — the editor hides everything else. */
export interface TemplateInputs {
  /** Max screenshot slots (0 = layout ignores screenshots) */
  screenshots: number
  /** Key art image with zoom/offset sliders */
  keyArt: boolean
  icon: boolean
}

export const TEMPLATE_INPUTS: Record<CreativeTemplateId, TemplateInputs> = {
  keyArt: {screenshots: 0, keyArt: true, icon: false},
  fan: {screenshots: 3, keyArt: false, icon: false},
  cascade: {screenshots: 3, keyArt: false, icon: false},
  strip: {screenshots: 3, keyArt: false, icon: false},
  splitText: {screenshots: 1, keyArt: false, icon: false},
  iconHeadline: {screenshots: 0, keyArt: false, icon: true},
  iconStack: {screenshots: 0, keyArt: false, icon: true},
  gameplay: {screenshots: 1, keyArt: false, icon: false},
}

// ── Video ──

/** Placements Apple accepts a video for (Universal is image-only). */
export const VIDEO_PLACEMENT_IDS: readonly CreativePlacementId[] = ['header', 'search']

/**
 * The animation's secondary option. Meaning depends on the type: an axis (pan, slide, float), a zoom focus point,
 * a one-way scroll direction, or a slideshow transition. Loops are ping-pong, so "left vs right" or "in vs out" would look identical.
 */
export type CreativeAnimationDirection =
  | 'horizontal'
  | 'vertical'
  | 'diagonal'
  | 'circle'
  | 'center'
  | 'left'
  | 'right'
  | 'up'
  | 'down'
  | 'fade'
  | 'blur'
  | 'push'
  | 'cover'
  | 'scale'

/** Background animation: camera pan (image) / angle sweep (gradient), or slow zoom (image). */
export type CreativeBgAnimationType = 'none' | 'pan' | 'zoom'

/** Foreground content animation. */
export type CreativeContentAnimationType = 'none' | 'float' | 'slide' | 'slideshow' | 'scroll'

export interface CreativeAnimation<T extends string> {
  type: T
  direction: CreativeAnimationDirection
  /** Full cycles per video loop — an integer so the loop stays seamless */
  speed: number
}

export interface CreativeAnimationSettings {
  bg: CreativeAnimation<CreativeBgAnimationType>
  content: CreativeAnimation<CreativeContentAnimationType>
}

export const BG_ANIMATION_TYPES: readonly CreativeBgAnimationType[] = ['none', 'pan', 'zoom']
export const CONTENT_ANIMATION_TYPES: readonly CreativeContentAnimationType[] = ['none', 'float', 'slide']

/** Directions that make sense for each animation type (none → no direction). */
export const ANIMATION_DIRECTIONS: Record<
  CreativeBgAnimationType | CreativeContentAnimationType,
  readonly CreativeAnimationDirection[]
> = {
  none: [],
  pan: ['horizontal', 'vertical', 'diagonal'],
  slide: ['horizontal', 'vertical', 'diagonal'],
  float: ['vertical', 'horizontal', 'circle'],
  zoom: ['center', 'left', 'right', 'up', 'down'],
  scroll: ['left', 'right'],
  slideshow: ['fade', 'blur', 'push', 'cover', 'scale'],
}

/** Layouts that can cycle their screenshots one at a time (the slideshow shows a single hero screenshot). */
export const SLIDESHOW_TEMPLATES: readonly CreativeTemplateId[] = ['fan', 'cascade', 'strip', 'splitText']

export const ANIMATION_MIN_SPEED = 1
export const ANIMATION_MAX_SPEED = 4

export const VIDEO_MIN_DURATION_SEC = 5
export const VIDEO_MAX_DURATION_SEC = 30
export const VIDEO_DEFAULT_DURATION_SEC = 10
/**
 * Output scales tried in order. Search Results videos may be 1920 × 1280 – 3840 × 2560 (3:2); the Header video is
 * fixed at 3840 × 1646, so it has no smaller fallback.
 */
export const VIDEO_OUTPUT_SCALES: Record<CreativePlacementId, readonly number[]> = {
  header: [1],
  search: [1, 0.5],
  universal: [1],
}
export const VIDEO_FPS_OPTIONS: readonly number[] = [30, 60]
export const VIDEO_BITRATE = 24_000_000
// H.264 High profile levels 6.0 / 5.2 / 5.1 — first one the browser encoder supports (3840 × 2560 needs 6.0)
export const VIDEO_CODEC_CANDIDATES: readonly string[] = ['avc1.64003c', 'avc1.640034', 'avc1.640033']

export const DEFAULT_CREATIVE_GRADIENT = {from: '#2b1b6b', to: '#7c5cff', angle: 120}
